import { Component, OnInit } from '@angular/core';
import { ProductListBusinessService } from '../../resources/product-list-business.service';
import { ProductItem, ProductListItem } from '../../resources/product-list-item.model';
import { ProductListGridDefinition } from './product-list-grid-definition.service';
import { Router, ActivatedRoute } from '@angular/router';
import { BuilderNavigationService } from '@gh/builder-shared';
import { AppRouter, getRouteInitData } from '@gh/common-shared';
import { cursorTracker, newGridPaginationCursor } from '@gh/core-ui-grid';
import { RouteDataService, SlicedDataSource, controlTracker } from '@gh/core-data';
import {
  DialogService,
  NotificationService,
  Confirmation,
  ConfirmationDialogConfig,
  yesNoConfirmation,
  yesAnswer,
  newFormControl,
  WaitingService,
} from '@gh/core-ui';
import {
  ListActionPipelines,
  CommonActions,
  BuilderUserContext,
  BuilderUserProfileStore,
} from '@gh/builder-shared';
import { SystemVarsRestService } from '@gh/builder-shared-data';
import { BuilderUserProfileDto } from '@gh/builder-shared-data';
import { filter$, finalize$, switchMap$ } from '@gh/rx/operators';
import { EMPTY$, of$ } from '@gh/rx';
import { Observable } from 'rxjs';
import { UnpaidInvoicesDialogComponent, UnpaidInvoicesDialogData } from '@gh/builder-shared';

@Component({
  standalone: false,
  selector: 'gh-product-list',
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.scss'],
  providers: [ProductListGridDefinition]
})
export class ProductListComponent implements OnInit {
  dataSource: SlicedDataSource<ProductListItem>;
  cursor = newGridPaginationCursor();
  showDeleted = newFormControl(false);

  constructor(
    private businessService: ProductListBusinessService,
    private gridDef: ProductListGridDefinition,
    private router: Router,
    private routeData: RouteDataService,
    private route: ActivatedRoute,
    private navigationService: BuilderNavigationService,
    private appRouter: AppRouter,
    private dialogService: DialogService,
    private pipelines: ListActionPipelines,
    private commonActions: CommonActions,
    private notificationService: NotificationService,
    private userContext: BuilderUserContext,
    private systemVarsRest: SystemVarsRestService,
    private userProfileStore: BuilderUserProfileStore,
    private waitings: WaitingService,
  ) {
  }
  
  ngOnInit(): void {
    const { gridLayout } = getRouteInitData(this.route) || {};
    const layoutToBind = Array.isArray(gridLayout) ? { children: gridLayout } : gridLayout;
    this.gridDef.bind(layoutToBind);
    
    const tracker = this.routeData.tracker(controlTracker(this.showDeleted)).withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(() => {
      this.loadData();
    });
  }

  reload(): void {
    // Handled by the tracker.
  }

  onAdd(): void {
    this.businessService.findAll({ showDeleted: false }).subscribe((products: ProductListItem[]) => {
      if (products.some(product => product.active)) {
        this.dialogService.warning('A plan is already active. Only one plan can be active at a time.');
        return;
      }
      this.appRouter.navigateToChild(['/setup/product/details', 'new']);
    });
  }

  loadData(): void {
    this.dataSource = this.businessService.findAllAsDataSource({
      showDeleted: this.showDeleted.value,
    });
  }
  
  goToAel(entityId?: string) {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: 'PRODUCT',
      label: 'lbl_product_list',
    });
  }

  onEdit(product: ProductItem): void {
    this.appRouter.navigateToChild(['/setup/product/details', product.id]);
  }

  onDeactivate(product: ProductListItem): void {
    this.businessService.deactivate(product.id, false).pipe(this.waitings.operator()).subscribe((result: ProductListItem) => {
      const hasUnpaidInvoices = result.unpaidInvoices && result.unpaidInvoices.length > 0;
      
      if (hasUnpaidInvoices) {
        const input: UnpaidInvoicesDialogData = {
          unpaidInvoices: result.unpaidInvoices!,
          productName: (result as any).name || product.name,
          customMessage: 'There are unpaid invoices associated with active subscriptions for this plan. Please review them before deactivating.',
          showProceedButton: true
        };
        const dialogRef = this.dialogService.openWithInput(UnpaidInvoicesDialogComponent, input, { disableClose: true });

        dialogRef.afterClosed().subscribe((acknowledged) => {
          if (acknowledged) {
            this.showDeactivationConfirmation(product, true);
          }
        });
      } else {
        this.showDeactivationConfirmation(product, true);
      }
    });
  }

  private showDeactivationConfirmation(product: ProductListItem, force: boolean): void {
    this.dialogService.confirmation({ 
      messageId: 'msg_product_deactivation',
      yesLabel: 'Deactivate Plan'
    }).subscribe((result) => {
      if (result === Confirmation.Yes) {
        this.executeDeactivate(product, force);
      }
    });
  }

  private executeDeactivate(product: ProductListItem, force: boolean = false): void {
    this.businessService.deactivate(product.id, force).pipe(this.waitings.operator()).subscribe((result: ProductListItem) => {
      this.notificationService.success(`Plan ${product.name} deactivated successfully.`);
      this.loadData();
    });
  }

  getDataSource(): any[] {
    // No longer used, but kept for reference
    return [];
  }

  get isStripeEnabled(): boolean {
    const user = this.userContext.data as BuilderUserProfileDto;
    return user?.isStripeEnabled ?? false;
  }

  get canToggleStripeStatus(): boolean {
    const user = this.userContext.data as BuilderUserProfileDto;
    return user?.canToggleStripeStatus ?? false;
  }

  isTogglingStripe: boolean = false;

  onToggleStripe(): void {
    if (this.isTogglingStripe) {
      return;
    }

    const currentState = this.isStripeEnabled;
    this.isTogglingStripe = true;
    
    const checkAndProceed$: Observable<boolean> = currentState
      ? this.businessService.findAll().pipe(
          this.waitings.operator(),
          switchMap$((products: ProductListItem[]) => {
            if (products.some(product => product.active)) {
              this.dialogService.warning('msg_stripe_disable_active_plan_warning');
              return EMPTY$;
            }
            return of$(true);
          })
        )
      : of$(true);

    checkAndProceed$.pipe(
      switchMap$(() => this.showConfirmationDialog(currentState)),
      filter$(yesAnswer),
      switchMap$(() => this.systemVarsRest.toggleStripeRegionStatus(!currentState).pipe(this.waitings.operator())),
      finalize$(() => {
        this.isTogglingStripe = false;
      })
    ).subscribe(
      (response: {isStripeEnabled: boolean}) => {
        this.userProfileStore.load({ path: location.pathname, query: location.search });
        const messageId = currentState ? 'msg_stripe_disabled_successfully' : 'msg_stripe_enabled_successfully';
        this.dialogService.success(messageId);
      },
      (error: any) => {
        this.dialogService.error('msg_stripe_toggle_failed');
      }
    );
  }

  private showConfirmationDialog(currentState: boolean): Observable<Confirmation> {
    const messageId = currentState ? 'msg_stripe_disable_confirmation' : 'msg_stripe_enable_confirmation';
    const config: ConfirmationDialogConfig = yesNoConfirmation(messageId);
    return this.dialogService.confirmation(config);
  }
}

